import { useEffect, useState } from "react";
import { Document, Page } from "react-pdf";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  azureDeleteBlob,
  azureUploadFile,
  getAzureBlobFile,
  saveContainerWeight,
} from "../services/container";
import { LoadingScreen } from "./loader/LoadingScreen";
import { useTranslation } from "react-i18next";
import { downloadFile } from "../shared/utils/utils";
import StatusBadge from "../ui/components/StatusBadge";
import { ActivityShareDownloadIcon } from "../containers/activity/components/ActivityShareExportItems";

const getFileDisplayName = (file) => {
  if (!file) return "";
  const parts = String(file).split("/");
  return parts[parts.length - 1] || file;
};

const truncateFileName = (name, maxLength = 35) => {
  if (!name || name.length <= maxLength) return name;
  return `${name.slice(0, maxLength)}...`;
};

const DocumentFileIcon = () => (
  <svg
    width="16"
    height="20"
    viewBox="0 0 16 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M4 16H12V14H4V16ZM4 12H12V10H4V12ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H10L16 6V18C16 18.55 15.8042 19.0208 15.4125 19.4125C15.0208 19.8042 14.55 20 14 20H2ZM9 7V2H2V18H14V7H9Z"
      fill="#222222"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="11"
    height="12"
    viewBox="0 0 11 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M2 12C1.63333 12 1.31944 11.8694 1.05833 11.6083C0.797222 11.3472 0.666667 11.0333 0.666667 10.6667V2H0V0.666667H3.33333V0H7.33333V0.666667H10.6667V2H10V10.6667C10 11.0333 9.86945 11.3472 9.60833 11.6083C9.34722 11.8694 9.03333 12 8.66667 12H2ZM8.66667 2H2V10.6667H8.66667V2ZM3.33333 9.33333H4.66667V3.33333H3.33333V9.33333ZM6 9.33333H7.33333V3.33333H6V9.33333Z"
      fill="#222222"
    />
  </svg>
);

const FilePanel = ({
  data,
  outputClient,
  setOutputClient,
  files,
  setFiles,
  superContainers,
  clientName,
  clientCode,
  clientAddress,
  tags,
  clientRegisteredWeight,
  comments,
  wasteCategoryName,
  checkStatus,
  cameraId,
  toCharacterization,
  clientId,
  embedded = false,
  presentation = "default",
  hideTitle = false,
  onClose,
  onDocViewerChange,
}) => {
  const { t } = useTranslation();
  const azureUrl = process.env.REACT_APP_AZURE_URL;
  const [isLoading, setIsLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [docViewer, setDocViewer] = useState(false);
  const [numPages, setNumPages] = useState();
  const [currentFile, setCurrentFile] = useState("");
  const [error, setError] = useState(null);
  const isFollowUp = presentation === "followUp";
  const fileInputId = embedded
    ? isFollowUp
      ? "fileInputFollowUp"
      : "fileInputEmbedded"
    : "fileInput";

  useEffect(() => {
    onDocViewerChange?.(docViewer);
  }, [docViewer, onDocViewerChange]);

  const onDocumentLoadSuccess = ({ numPages: pageCount }) => {
    setNumPages(pageCount);
  };

  const handleClose = () => {
    setError(null);
    if (docViewer) {
      setDocViewer(false);
    } else if (typeof onClose === "function") {
      onClose();
    }
  };

  const updateOutputClient = async (
    newFiles,
    containerId,
    resolvedCameraId,
  ) => {
    const timestamp = data._time.replace(/:/g, "_").slice(0, -1);
    const outputClientBlob = `camera-ak_${resolvedCameraId}/output-client/${timestamp}-camera-ak_${resolvedCameraId}-${containerId}-client.json`;
    const newOutputClient = {
      ...outputClient,
      id: containerId,
      time: data._time,
      weight:
        parseFloat(String(clientRegisteredWeight).replace(",", ".")) * 1000,
      blobName: outputClientBlob,
      waste_category: wasteCategoryName,
      comments: JSON.stringify(comments),
      toCharacterization: toCharacterization,
      mongoClientId: clientId,
      client: JSON.stringify({
        name: clientName,
        code: clientCode,
        address: clientAddress,
        checkStatus: checkStatus
          ? typeof checkStatus === "string" &&
            checkStatus.trim().startsWith("{")
            ? JSON.parse(checkStatus)
            : checkStatus
          : "-",
        tags: tags ? tags : [],
        files: newFiles,
      }),
    };

    await saveContainerWeight(newOutputClient);
    setFiles(newFiles);
    setOutputClient(newOutputClient);
  };

  const getContainerId = () => {
    if (
      superContainers &&
      Object.keys(superContainers).length > 0 &&
      superContainers.containerObjectIdList.includes(data._id)
    ) {
      return superContainers._id;
    }
    return data._id;
  };

  const getBlobName = (resolvedCameraId, containerId, fileName) => {
    const timestamp = data._time.replace(/:/g, "_").slice(0, -1);
    return `camera-ak_${resolvedCameraId}/files/${containerId}/${timestamp}/${fileName}`;
  };

  const deleteFile = async (file) => {
    setIsLoading(true);

    const newFiles = files.filter((item) => item !== file);
    const containerId = getContainerId();

    await azureDeleteBlob({ blobName: file });
    await updateOutputClient(newFiles, containerId, cameraId);

    setIsLoading(false);
  };

  const onFileChange = async (event) => {
    setError(null);
    setIsLoading(true);

    const file = event.target.files[0];
    if (!file) {
      setIsLoading(false);
      return;
    }
    const containerId = getContainerId();
    const blobName = getBlobName(cameraId, containerId, file.name);

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = async () => {
      try {
        const base64File = reader.result.split(",")[1];
        const response = await azureUploadFile({ base64File, blobName });

        if (response.status === 200) {
          const newFiles = Array.from(new Set([...files, blobName]));
          await updateOutputClient(newFiles, containerId, cameraId);
        }
      } catch (uploadError) {
        if (uploadError.response?.status === 413) {
          setError(uploadError.response.data.message);
        }
        console.error("File upload failed:", uploadError);
      } finally {
        setIsLoading(false);
        event.target.value = "";
      }
    };
  };

  const toggleDocViewer = async (file) => {
    setError(null);
    setIsLoading(true);
    setCurrentFile(getFileDisplayName(file));
    try {
      const response = await getAzureBlobFile({ blobUrl: azureUrl + file });
      setPdfBlob(response.data);
      setDocViewer(true);
      setIsLoading(false);
    } catch (viewerError) {
      console.error("Error downloading PDF:", viewerError);
      setIsLoading(false);
    }
  };

  const handleDownloadListedFile = async (file) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await getAzureBlobFile({ blobUrl: azureUrl + file });
      downloadFile(response.data, getFileDisplayName(file));
    } catch (downloadError) {
      console.error("Error downloading PDF:", downloadError);
    } finally {
      setIsLoading(false);
    }
  };

  const pdfViewer = docViewer ? (
    <div className="w-full flex justify-center bg-[#D4D4D7] rounded-lg">
      <Document
        file={pdfBlob}
        onLoadSuccess={onDocumentLoadSuccess}
        className=""
      >
        {Array.from(new Array(numPages), (el, index) => (
          <div key={`page_wrapper_${index + 1}`} className="my-4">
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={Math.min(window.innerWidth * 0.6, 800)}
            />
          </div>
        ))}
      </Document>
    </div>
  ) : null;

  if (isFollowUp) {
    const importControl = (
      <>
        <label
          htmlFor={fileInputId}
          className={`char-button char-button-primary ${
            isLoading ? "opacity-50 pointer-events-none" : "cursor-pointer"
          }`}
        >
          {isLoading ? (
            <LoadingScreen />
          ) : (
            <>
              <span className="char-button-icon char-button-icon--inverted">
                <ActivityShareDownloadIcon />
              </span>
              <span>{t("Import")}</span>
              <i
                className="fas fa-chevron-down char-button-chevron"
                aria-hidden
              />
            </>
          )}
        </label>
        <input
          id={fileInputId}
          hidden
          accept="application/pdf"
          type="file"
          onChange={onFileChange}
        />
      </>
    );

    return (
      <div className="w-full">
        {error && (
          <div className="px-6 pt-4" style={{ color: "red" }}>
            Error: {error}
          </div>
        )}

        {(docViewer || !hideTitle) && (
          <div
            className={`flex flex-wrap items-center gap-3 px-6 ${
              hideTitle ? "pt-2 pb-4 justify-end" : "pt-6 pb-4 justify-between"
            }`}
          >
            {!hideTitle ? (
              <h3 className="char-section-header-title !mb-0">
                {docViewer ? currentFile : t("Imported documents")}
              </h3>
            ) : docViewer ? (
              <span className="mr-auto text-sm font-medium text-[#222222]">
                {currentFile}
              </span>
            ) : null}

            {docViewer ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="char-button char-button-ghost"
                  onClick={() =>
                    downloadFile(pdfBlob, currentFile || "document.pdf")
                  }
                >
                  {t("Download")}
                </button>
                <button
                  type="button"
                  className="char-button char-button-ghost"
                  onClick={() => setDocViewer(false)}
                >
                  {t("Back")}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {docViewer ? (
          <div className="px-6 pb-6">{pdfViewer}</div>
        ) : (
          <>
            <div className="flex flex-col gap-3 px-6">
              {(files || []).length === 0 ? (
                <p className="m-0 text-sm text-[#6B7280]">
                  {t("No files yet")}
                </p>
              ) : (
                files.map((file) => {
                  const displayName = getFileDisplayName(file);
                  const shortName = truncateFileName(displayName);
                  return (
                    <div
                      key={file}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#D3D3D3] bg-white px-4 py-3"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <DocumentFileIcon />
                        <button
                          type="button"
                          title={displayName}
                          className="min-w-0 border-0 bg-transparent p-0 text-left text-[14px] font-bold text-[#222222] underline cursor-pointer"
                          onClick={() => toggleDocViewer(file)}
                        >
                          {shortName}
                        </button>
                        <StatusBadge status="Imported" />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          className="char-button char-button-ghost char-button--table-action"
                          onClick={() => handleDownloadListedFile(file)}
                          disabled={isLoading}
                        >
                          {t("Download")}
                        </button>
                        <button
                          type="button"
                          aria-label={t("Delete")}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#D3D3D3] bg-white cursor-pointer hover:bg-[#F8F8F8]"
                          onClick={() => deleteFile(file)}
                          disabled={isLoading}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="flex justify-end px-6 pt-4 pb-6">
              {importControl}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={embedded ? "w-full" : undefined}>
      {error && (
        <div style={{ color: "red", marginTop: "10px" }}>Error: {error}</div>
      )}
      <div
        className={
          embedded
            ? "flex justify-between items-center px-4 py-3 bg-white rounded-lg border border-gray-200 mb-4"
            : "flex justify-between items-center"
        }
      >
        <span className="text-blue">
          {docViewer ? currentFile : t("My files")}
        </span>
        <div style={{ display: "flex", gap: "10px" }}>
          {docViewer && (
            <>
              <FileDownloadIcon
                className="text-[var(--gray)] cursor-pointer"
                onClick={() => downloadFile(pdfBlob, currentFile)}
              />
              {embedded ? (
                <ArrowBackIcon
                  className="text-[var(--gray)] cursor-pointer"
                  onClick={() => setDocViewer(false)}
                />
              ) : null}
            </>
          )}
          {!embedded ? (
            <CloseIcon
              className="text-[var(--gray)] cursor-pointer"
              onClick={handleClose}
            />
          ) : null}
        </div>
      </div>
      {docViewer ? (
        pdfViewer
      ) : (
        <>
          <div
            className={`flex flex-col bg-[#e5e7eb] p-2 gap-2 ${embedded ? "rounded-lg" : ""}`}
          >
            {files &&
              files.length > 0 &&
              files.map((file) => (
                <div
                  key={file}
                  className="flex flex-row w-full bg-white rounded-md h-10 items-center justify-between p-2"
                >
                  <span
                    className="cursor-pointer w-11/12 overflow-hidden text-ellipsis whitespace-nowrap"
                    onClick={() => {
                      toggleDocViewer(file);
                    }}
                  >
                    <i className="fas fa-file-pdf cursor-pointer text-gray-400 mr-2"></i>
                    {getFileDisplayName(file)}
                  </span>
                  <i
                    className="fas fa-trash-alt hover:text-gray-400 text-red cursor-pointer"
                    onClick={() => {
                      deleteFile(file);
                    }}
                  ></i>
                </div>
              ))}
          </div>
          <div
            className={`bg-[#e5e7eb] ${embedded ? "mt-2 rounded-lg py-3" : ""}`}
          >
            <div className="flex flex-row gap-3 text-blue w-full text-center text-lg justify-center cursor-pointer hover:text-[#00A7A6]">
              {isLoading ? (
                <LoadingScreen />
              ) : (
                <>
                  <label
                    htmlFor={fileInputId}
                    className="flex items-center cursor-pointer"
                  >
                    <span>{t("Upload file")}</span>
                    <i className="fas fa-upload ml-2"></i>
                  </label>
                  <input
                    id={fileInputId}
                    hidden
                    accept="application/pdf"
                    type="file"
                    onChange={onFileChange}
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FilePanel;
